import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin/admin.service';
import { CourseService } from 'src/app/services/course/course.service';
import { CalendarOptions } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/angular/daygrid';
import timeGridPlugin from '@fullcalendar/angular/timegrid';
import interactionPlugin from '@fullcalendar/angular/interaction';
import themePlugin from '@fullcalendar/angular/themes/monarch';
import { XCourseFilter } from 'src/app/core/common/XCourseFilter';


/* type CalendarView =
  | 'dayGridMonth'
  | 'timeGridWeek'
  | 'timeGridDay'; */

type CalendarView =
  | 'dayGridMonth'
  | 'dayGridWeek'
  | 'dayGridDay';



interface CorporateParticipant {

  fullName: string;

  uocIds: Array<number | null>;

  vocIds: Array<number | null>;

  fitTestRequired: boolean;

  // Store the course occurrence date selected from the participant calendar.
  calendarDate: string;

  // Store the selected calendar occurrence so the same selection is restored when reopened.
  calendarOccurrenceId: number | null;

  // Store the primary UOC course selected from the calendar occurrence.
  calendarCourseId: number | null;
}


interface CorporateOtherRow {

  values: string[];
}

@Component({
  selector: 'app-x-course-multi-form',
  templateUrl: './x-course-multi-form.component.html',
  styleUrl: './x-course-multi-form.component.scss'
})
export class XCourseMultiFormComponent implements OnInit, OnDestroy {

  // Track Book Now submission
  isBookNowSubmitted: boolean = false;


  // Store all UOC courses
  allUOCCourses: any[] = [];

  // Store all VOC courses
  allVOCCourses: any[] = [];


  // Store participant count
  numberOfParticipants = 1;

  // Store UOC column count
  numberOfUOC = 2;

  // Store VOC column count
  numberOfVOC = 2;

  // Store other row count
  numberOfOtherRows = 1;


  // Store other column numbers
  otherColumns: number[] = [
    1,
    2,
    3,
    4,
    5
  ];


  // Store corporate participants
  participants:
    CorporateParticipant[] = [];

  // Store corporate other rows
  otherRows:
    CorporateOtherRow[] = [];

  // Store additional comments
  additionalComments = '';


  // Control participant calendar modal
  showParticipantCalendarModal = false;

  // Control participant course selection modal
  showParticipantCourseSelectionModal = false;

  // Store selected participant index
  participantCalendarIndex: number | null = null;

  // Store selected calendar occurrence
  selectedParticipantOccurrence: any = null;

  // Store participant calendar courses
  participantCalendarCourses: any[] = [];

  // Store temporary UOC selections
  participantModalUOCIds: number[] = [];

  // Store temporary VOC selections
  participantModalVOCIds: number[] = [];

  // Track participant calendar loading state
  isParticipantCalendarLoading = false;

  // Store participant calendar error message
  participantCalendarErrorMessage = '';

  // Store participant calendar title
  participantCalendarTitle = '';

  // Store selected participant calendar date
  participantSelectedDate = '';

  // Store active participant calendar view
  //participantActiveCalendarView: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' = 'dayGridMonth';
  participantActiveCalendarView:
    'dayGridMonth' |
    'dayGridWeek' |
    'dayGridDay'
    = 'dayGridMonth';

  // Control participant calendar filter panel
  participantIsFilterPanelVisible = true;

  // Store participant course type filters
  participantCourseTypeFilters: any[] = [];

  // Store selected participant location
  participantSelectedLocation = '';

  // Store participant available locations
  participantAvailableLocations: string[] = [];

  // Store filtered participant calendar courses
  participantFilteredCalendarCourses: any[] = [];

  // Store temporary UOC dropdown value
  participantSelectedUOCId: number | null = null;

  // Store temporary VOC dropdown value
  participantSelectedVOCId: number | null = null;


  // Track corporate booking save state
  isSavingCorporateBooking =
    false;

  // Store corporate success message
  corporateSuccessMessage =
    '';

  // Store corporate error message
  corporateErrorMessage =
    '';


  // Store company name
  companyName = '';

  // Store company email
  companyEmail = '';

  // Store requested by name
  requestedBy = '';

  // Store requested by phone
  requestedByPhone = '';

  // Store onsite supervisor name
  onSiteSupervisor = '';

  // Store onsite supervisor phone
  onSiteSupervisorPhone = '';

  // Store site location address
  siteLocationAddress = '';

  // Store site location selection
  siteLocationSelection = '';

  // Store requested date
  dateRequested: string =
    new Date()
      .toISOString()
      .split('T')[0];

  // Store required training date
  dateTrainingRequired = '';

  // Store today's date
  todayDate: string =
    new Date()
      .toISOString()
      .split('T')[0];

  // Store copy of awards requirement
  copyOfAwardsRequired:
    'YES' | 'NO' = 'YES';

  // Store metro client status
  isMetroClient:
    'YES' | 'NO' = 'NO';

  // Store Pegasus upload requirement
  uploadAwardEvidenceToPegasus:
    'YES' | 'NO' = 'NO';

  // Store site locations
  siteLocations: any[] = [];

  // Track site location loading state
  isLoadingSiteLocations =
    false;

  // Control enquiry success popup
  showEnquirySuccessPopup: boolean = false;




  // Configure the participant calendar with the same options and rendering used by the public Course Calendar.
  participantCalendarOptions: CalendarOptions = {

    plugins: [
      themePlugin,
      dayGridPlugin,
      timeGridPlugin,
      interactionPlugin
    ],

    initialView: 'dayGridMonth',

    headerToolbar: false,

    height: 'auto',

    fixedWeekCount: false,

    showNonCurrentDates: true,

    displayEventTime: false,

    dayMaxEvents: 2,

    moreLinkText: (count) =>
      `+${count} more`,


    eventDisplay: 'block',

    nowIndicator: true,

    locale: 'en-AU',

    slotMinTime: '07:00:00',

    slotMaxTime: '19:00:00',

    slotDuration: '00:30:00',

    allDaySlot: true,

    events: [],


    // ==========================================================
    // DATE RANGE CHANGE
    // ==========================================================

    datesSet: (info) => {

      /*
       * Set asynchronously to avoid:
       * ExpressionChangedAfterItHasBeenCheckedError
       */
      setTimeout(() => {

        this.participantCalendarTitle =
          info.view.title;

        this.participantActiveCalendarView =
          info.view.type as CalendarView;

      });


      const startDate =
        this.toParticipantDateKey(
          info.start
        );


      /*
       * FullCalendar end date is exclusive.
       */
      const endDate =
        new Date(info.end);

      endDate.setDate(
        endDate.getDate() - 1
      );


      this.loadParticipantCalendarCourses(
        startDate,
        this.toParticipantDateKey(endDate)
      );
    },


    // ==========================================================
    // DATE CLICK
    // ==========================================================

    dateClick: (info) => {

      this.selectParticipantCalendarDate(
        info.dateStr
      );
    },


    // ==========================================================
    // EVENT CLICK
    // ==========================================================

    eventClick: (info) => {

      const selectedCourse =
        this.participantFilteredCalendarCourses.find(
          course =>
            String(course.id) ===
            String(info.event.id)
        );

      if (selectedCourse) {

        this.openParticipantCourseSelection(
          selectedCourse
        );
      }
    },


    // ==========================================================
    // SELECTED DAY
    // ==========================================================

    dayCellClass: (info) => {

      return (
        this.toParticipantDateKey(info.date) ===
        this.participantSelectedDate
      )
        ? 'ecb-course-calendar-selected-day'
        : '';
    },


    // ==========================================================
    // EVENT MOUNT
    // ==========================================================

    eventDidMount: (info) => {

      const renderedCourse =
        this.participantFilteredCalendarCourses.find(
          course =>
            String(course.id) ===
            String(info.event.id)
        );


      const backgroundColor =
        renderedCourse?.backgroundColor ??
        '#eef2ff';

      const textColor =
        renderedCourse?.textColor ??
        '#4338ca';


      this.applyParticipantEventElementColors(
        info.el,
        backgroundColor,
        textColor
      );


      // Keep the participant's saved/temporary occurrence visibly selected when the calendar is reopened.
      const savedOccurrenceId =
        this.participantCalendarIndex !== null
          ? this.participants[this.participantCalendarIndex]?.calendarOccurrenceId
          : null;

      const isSelectedOccurrence =
        Number(this.selectedParticipantOccurrence?.id) === Number(renderedCourse?.id) ||
        Number(savedOccurrenceId) === Number(renderedCourse?.id);

      if (isSelectedOccurrence) {
        info.el.style.boxShadow =
          '0 0 0 3px rgba(241, 90, 36, 0.45)';
      }
    },


    // ==========================================================
    // EVENT CONTENT
    // ==========================================================

    eventContent: (info) => {

      const renderedCourse =
        this.participantFilteredCalendarCourses.find(
          course =>
            String(course.id) ===
            String(info.event.id)
        );


      const backgroundColor =
        renderedCourse?.backgroundColor ??
        '#eef2ff';

      const textColor =
        renderedCourse?.textColor ??
        '#4338ca';


      const eventWrapper =
        document.createElement('div');

      eventWrapper.className =
        'ecb-course-calendar-event-content';

      eventWrapper.style.width =
        '100%';

      eventWrapper.style.height =
        '100%';

      eventWrapper.style.boxSizing =
        'border-box';

      eventWrapper.style.backgroundColor =
        backgroundColor;

      eventWrapper.style.color =
        textColor;


      const codeElement =
        document.createElement('strong');

      codeElement.className =
        'ecb-course-calendar-event-code';

      codeElement.textContent =
        info.event.extendedProps[
        'calendarCode'
        ] ?? renderedCourse?.courseCode ?? '';


      const nameElement =
        document.createElement('span');

      nameElement.className =
        'ecb-course-calendar-event-name';

      nameElement.textContent =
        info.event.extendedProps[
        'courseName'
        ] ?? renderedCourse?.courseName ?? info.event.title;


      eventWrapper.append(
        codeElement,
        nameElement
      );


      return {
        domNodes: [
          eventWrapper
        ]
      };
    }
  };


  constructor(
    private adminService:
      AdminService,
    private courseService:
      CourseService,
    private router: Router
  ) {
  }


  // ============================================================
  // INIT
  // ============================================================

  ngOnInit(): void {

    this.loadDropdownUOCCourses();

    this.loadVOCCourses();

    this.resetCorporateForm();

    this.loadSiteLocations();
  }

  ngOnDestroy(): void {
    if (this.dropdownSearchTimer) {
      clearTimeout(this.dropdownSearchTimer);
    }
    this.onCourseDropdownClosed();
  }


  // ============================================================
  // UOC COLUMN ARRAY
  // ============================================================

  get uocColumns(): number[] {

    return Array.from(
      {
        length:
          this.numberOfUOC
      },
      (_, index) =>
        index + 1
    );
  }


  // ============================================================
  // VOC COLUMN ARRAY
  // ============================================================

  get vocColumns(): number[] {

    return Array.from(
      {
        length:
          this.numberOfVOC
      },
      (_, index) =>
        index + 1
    );
  }


  // ============================================================
  // LOAD UOC COURSES
  // ============================================================


  dropdownPageSize = 50;
  dropdownTotalRecords = 0;
  dropdownSearchText = '';
  dropdownLoading = false;

  private loadedDropdownPages = new Set<number>();
  private loadingDropdownPages = new Set<number>();
  private dropdownRequestVersion = 0;
  private dropdownSearchTimer: ReturnType<typeof setTimeout> | null = null;
  dropdownHasLoaded = false;

  // Preserve selected UOCs when the dropdown search or page changes.
  private uocCourseCache = new Map<number, any>();

  // Stable options for participant row dropdowns (do not recreate on every Angular check).
  private participantUOCOptionsCache = new WeakMap<CorporateParticipant, Map<number, {
    source: any[];
    selectionKey: string;
    options: any[];
  }>>();

  // One scrollbar handles progressive loading in an opened PrimeNG dropdown.
  private dropdownScrollContainer: HTMLElement | null = null;
  private dropdownScrollAttachTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly handleDropdownScroll = (): void => {
    const list = this.dropdownScrollContainer;
    if (list && list.scrollTop + list.clientHeight >= list.scrollHeight - 70) {
      this.onCourseDropdownScroll();
    }
  };

  // ============================================================
  // INITIALISE DROPDOWN
  // ============================================================

  loadDropdownUOCCourses(): void {

    this.dropdownRequestVersion++;

    this.loadedDropdownPages.clear();
    this.loadingDropdownPages.clear();

    this.allUOCCourses = [];
    this.dropdownTotalRecords = 0;
    this.dropdownLoading = false;
    this.dropdownHasLoaded = false;

    this.loadDropdownPage(1);
  }


  // ============================================================
  // LOAD DROPDOWN COURSES WITH SERVER-SIDE PAGINATION
  // ============================================================

  private loadDropdownPage(page: number): void {

    // Prevent invalid page requests
    if (page < 1) {
      return;
    }

    // Prevent multiple requests at the same time
    if (this.dropdownLoading) {
      return;
    }

    // Prevent loading an already loaded page
    if (this.loadedDropdownPages.has(page)) {
      return;
    }

    // Stop when all matching records have been loaded
    if (
      this.dropdownTotalRecords > 0 &&
      this.allUOCCourses.length >= this.dropdownTotalRecords
    ) {
      return;
    }

    // Remember request version to ignore old responses
    const requestVersion = this.dropdownRequestVersion;

    // Prepare existing XCourseFilter
    const filter: XCourseFilter = {
      action: 'GET_ALL_DROPDOWN_PAGED',
      type: '',
      search: this.dropdownSearchText?.trim() || '',
      searchKey: '',
      isActive: true,
      stateCode: '',
      currentPage: page,
      pageSize: this.dropdownPageSize
    };

    // Start loading
    this.dropdownLoading = true;

    // Mark page as loading
    this.loadingDropdownPages.add(page);

    // Request only the selected page
    this.adminService
      .getXCourseDropdownPage(filter)
      .subscribe({

        next: (response) => {

          // Ignore outdated responses after location/search changes
          if (
            requestVersion !== this.dropdownRequestVersion
          ) {
            return;
          }

          // Get paginated records
          const records = (response.records || []).filter(
            (course: any) => course != null && Number(course.id) > 0
          );

          // Keep full course details for saved selections, even after searches.
          records.forEach((course: any) => {
            this.uocCourseCache.set(Number(course.id), course);
          });
          this.dropdownHasLoaded = true;

          // Store total matching records from SQL
          this.dropdownTotalRecords =
            Number(response.totalRecords || 0);

          // Get IDs of already loaded courses
          const existingIds = new Set(
            this.allUOCCourses
              .filter(course => course != null)
              .map(course => course.id)
          );

          // Remove duplicate or null records
          const newRecords = records.filter(
            (course: any) =>
              course != null &&
              !existingIds.has(course.id)
          );

          // Append new records without null placeholders
          this.allUOCCourses = [
            ...this.allUOCCourses,
            ...newRecords
          ];

          // Mark page as loaded
          this.loadedDropdownPages.add(page);

          // Clear loading state
          this.loadingDropdownPages.delete(page);

          this.dropdownLoading = false;
        },

        error: (error) => {

          // Ignore outdated responses
          if (
            requestVersion !== this.dropdownRequestVersion
          ) {
            return;
          }

          console.error(
            'Error loading course dropdown page:',
            error
          );

          // Clear loading state
          this.loadingDropdownPages.delete(page);

          this.dropdownLoading = false;
        }

      });
  }

  // ============================================================
  // LOAD NEXT 50 COURSES (NO VIRTUAL-SCROLL PLACEHOLDERS)
  // ============================================================

  onCourseDropdownOpened(): void {
    if (!this.dropdownHasLoaded && !this.dropdownLoading) {
      this.loadDropdownPage(1);
    }

    // The panel is appended to document.body and has its own scroll element.
    this.onCourseDropdownClosed();
    this.dropdownScrollAttachTimer = setTimeout(() => {
      const panels = Array.from(
        document.querySelectorAll<HTMLElement>('.ecb-short-course-dropdown-panel')
      );
      const panel = panels.find(element => element.getClientRects().length > 0);
      const list = panel?.querySelector<HTMLElement>('.p-dropdown-items-wrapper');
      if (list) {
        this.dropdownScrollContainer = list;
        list.addEventListener('scroll', this.handleDropdownScroll, { passive: true });
      }
      this.dropdownScrollAttachTimer = null;
    }, 0);
  }

  onCourseDropdownClosed(): void {
    if (this.dropdownScrollAttachTimer) {
      clearTimeout(this.dropdownScrollAttachTimer);
      this.dropdownScrollAttachTimer = null;
    }
    this.dropdownScrollContainer?.removeEventListener('scroll', this.handleDropdownScroll);
    this.dropdownScrollContainer = null;
  }

  onCourseDropdownScroll(): void {
    if (this.dropdownLoading || !this.dropdownHasLoaded) {
      return;
    }

    if (this.allUOCCourses.length >= this.dropdownTotalRecords) {
      return;
    }

    // Load pages in sequence because the options are appended to the list.
    this.loadDropdownPage(this.loadedDropdownPages.size + 1);
  }


  // ============================================================
  // SERVER-SIDE DROPDOWN SEARCH
  // ============================================================

  onDropdownSearch(value: string): void {

    if (this.dropdownSearchTimer) {
      clearTimeout(this.dropdownSearchTimer);
    }

    this.dropdownSearchTimer = setTimeout(() => {

      this.dropdownSearchText = value.trim();

      this.loadDropdownUOCCourses();

    }, 350);
  }


  // ============================================================
  // LOAD VOC COURSES
  // ============================================================

  loadVOCCourses(): void {

    this.adminService
      .getXVOCList('GET_ALL')
      .subscribe({

        next: (response: any[]) => {

          this.allVOCCourses =
            response || [];

          console.log(
            'VOC Courses:',
            this.allVOCCourses
          );
        },


        error: (error) => {

          console.error(
            'Error loading VOC courses:',
            error
          );
        }
      });
  }


  // ============================================================
  // CREATE PARTICIPANT
  // ============================================================

  createParticipant():
    CorporateParticipant {

    return {

      fullName:
        '',


      /*
       * Every participant gets their
       * own UOC slots.
       *
       * Default:
       *
       * [
       *   null,
       *   null,
       *   null,
       *   null
       * ]
       */
      uocIds:
        Array(
          this.numberOfUOC
        ).fill(null),


      /*
       * Every participant gets their
       * own VOC slots.
       */
      vocIds:
        Array(
          this.numberOfVOC
        ).fill(null),


      fitTestRequired:
        false,

      // No calendar date has been selected for a new participant.
      calendarDate:
        '',

      // No occurrence has been selected for a new participant.
      calendarOccurrenceId:
        null,

      // No primary calendar course has been selected for a new participant.
      calendarCourseId:
        null,
    };
  }


  // ============================================================
  // CREATE OTHER ROW
  // ============================================================

  createOtherRow():
    CorporateOtherRow {

    return {

      /*
       * Five separate values.
       */
      values: [
        '',
        '',
        '',
        '',
        ''
      ]
    };
  }


  // ============================================================
  // UPDATE PARTICIPANT COUNT
  // ============================================================

  updateParticipantCount(): void {

    let count =
      Number(
        this.numberOfParticipants
      );


    if (
      !count ||
      count < 1
    ) {

      count = 1;
    }


    if (count > 100) {

      count = 100;
    }


    this.numberOfParticipants =
      count;


    // Add participant rows.
    while (
      this.participants.length <
      count
    ) {

      this.participants.push(
        this.createParticipant()
      );
    }


    // Remove participant rows.
    while (
      this.participants.length >
      count
    ) {

      this.participants.pop();
    }

    // Recalculate the earliest training date if participant rows were removed by the count field.
    this.updateDateTrainingRequiredFromParticipants();
  }


  // ============================================================
  // UPDATE UOC COUNT
  // ============================================================

  updateUOCCount(): void {

    let count =
      Number(
        this.numberOfUOC
      );


    if (
      !count ||
      count < 1
    ) {

      count = 1;
    }


    /*
     * Screenshot requirement:
     * maximum 5 UOC columns.
     */
    if (count > 5) {

      count = 5;
    }


    this.numberOfUOC =
      count;


    /*
     * Update UOC slots for
     * EVERY participant.
     */
    this.participants
      .forEach(
        participant => {


          // Add slots.
          while (
            participant
              .uocIds
              .length <
            count
          ) {

            participant
              .uocIds
              .push(
                null
              );
          }


          // Remove slots.
          while (
            participant
              .uocIds
              .length >
            count
          ) {

            participant
              .uocIds
              .pop();
          }

        }
      );
  }


  // ============================================================
  // UPDATE VOC COUNT
  // ============================================================

  updateVOCCount(): void {

    let count =
      Number(
        this.numberOfVOC
      );


    if (
      !count ||
      count < 1
    ) {

      count = 1;
    }


    if (count > 10) {

      count = 10;
    }


    this.numberOfVOC =
      count;


    /*
     * Update VOC slots for
     * EVERY participant.
     */
    this.participants
      .forEach(
        participant => {


          // Add slots.
          while (
            participant
              .vocIds
              .length <
            count
          ) {

            participant
              .vocIds
              .push(
                null
              );
          }


          // Remove slots.
          while (
            participant
              .vocIds
              .length >
            count
          ) {

            participant
              .vocIds
              .pop();
          }

        }
      );
  }


  // ============================================================
  // UPDATE OTHER ROW COUNT
  // ============================================================

  updateOtherRows(): void {

    let count =
      Number(
        this.numberOfOtherRows
      );


    if (
      isNaN(count) ||
      count < 0
    ) {

      count = 0;
    }


    if (count > 10) {

      count = 10;
    }


    this.numberOfOtherRows =
      count;


    // Add rows.
    while (
      this.otherRows.length <
      count
    ) {

      this.otherRows.push(
        this.createOtherRow()
      );
    }


    // Remove rows.
    while (
      this.otherRows.length >
      count
    ) {

      this.otherRows.pop();
    }
  }


  // ============================================================
  // ADD PARTICIPANT
  // ============================================================

  addParticipant(): void {

    this.participants.push(
      this.createParticipant()
    );


    this.numberOfParticipants =
      this.participants.length;
  }


  // ============================================================
  // REMOVE PARTICIPANT
  // ============================================================

  removeParticipant(
    index: number
  ): void {

    /*
     * Keep at least one participant.
     */
    if (
      this.participants.length <= 1
    ) {

      return;
    }


    this.participants.splice(
      index,
      1
    );

    // Recalculate the earliest training date when a participant row is removed.
    this.updateDateTrainingRequiredFromParticipants();


    this.numberOfParticipants =
      this.participants.length;
  }


  // ============================================================
  // ADD OTHER ROW
  // ============================================================

  addOtherRow(): void {

    this.otherRows.push(
      this.createOtherRow()
    );


    this.numberOfOtherRows =
      this.otherRows.length;
  }


  // ============================================================
  // REMOVE OTHER ROW
  // ============================================================

  removeOtherRow(
    index: number
  ): void {

    this.otherRows.splice(
      index,
      1
    );


    this.numberOfOtherRows =
      this.otherRows.length;
  }


  // ============================================================
  // TRACK BY INDEX
  // ============================================================

  /*
   * Important for editable primitive
   * string arrays.
   *
   * Prevents Angular from mixing
   * the 5 "Other" input controls.
   */
  trackByIndex(
    index: number
  ): number {

    return index;
  }

  // ============================================================
  // Back
  // ============================================================

  backForm(): void {
    this.isBookNowSubmitted = false;
  }


  // ============================================================
  // OPEN PARTICIPANT CALENDAR
  // ============================================================

  openParticipantCalendar(participantIndex: number): void {
    // Remember which participant row should receive the selected date and courses.
    this.participantCalendarIndex = participantIndex;
    // Clear any previous popup error before opening the calendar.
    this.participantCalendarErrorMessage = '';

    // Reopen on the participant's already selected date when one exists.
    const savedDate = this.participants[participantIndex]?.calendarDate;
    // Restore the same selected-day highlight used by the public calendar.
    this.participantSelectedDate = savedDate || '';
    this.participantCalendarOptions = {
      ...this.participantCalendarOptions,
      initialDate: savedDate || new Date()
    };

    // Show the participant calendar popup.
    this.showParticipantCalendarModal = true;
  }

  // ============================================================
  // CLOSE PARTICIPANT CALENDAR
  // ============================================================

  closeParticipantCalendar(): void {
    // Hide the calendar popup without changing the participant's saved selections.
    this.showParticipantCalendarModal = false;
  }

  // ============================================================
  // LOAD PARTICIPANT CALENDAR COURSES
  // ============================================================

  loadParticipantCalendarCourses(startDate: string, endDate: string): void {
    // Show the loading indicator while the calendar API is running.
    this.isParticipantCalendarLoading = true;
    // Reuse the existing calendar endpoint so corporate selection matches public availability.
    this.courseService.getXCourseCalendarList(startDate, endDate).subscribe({
      next: (response: any[]) => {
        // Convert the API response to the structure required by FullCalendar.
        this.participantCalendarCourses = (response || [])
          .map((item: any) => {
            const course = item?.course ?? item?.Course;
            const occurrence = item?.occurance ?? item?.Occurance;
            if (!course || !occurrence) {
              return null;
            }

            // Calendar-selected UOCs may be outside the loaded dropdown pages.
            // Retain the fields the calendar API actually supplies for booking.
            const courseId = Number(course.id ?? course.Id ?? 0);
            if (courseId > 0) {
              const existing = this.uocCourseCache.get(courseId) || {};
              this.uocCourseCache.set(courseId, {
                ...existing,
                id: courseId,
                courseCode: course.courseCode ?? course.CourseCode ?? existing.courseCode ?? '',
                courseTitle: course.courseTitle ?? course.CourseTitle ?? existing.courseTitle ?? '',
                description: course.description ?? course.Description ?? existing.description ?? '',
                totalAmount: course.totalAmount ?? course.TotalAmount ?? existing.totalAmount ?? null,
                currencyCode: course.currencyCode ?? course.CurrencyCode ?? existing.currencyCode ?? '',
                documentTypes: course.documentTypes ?? course.DocumentTypes ?? existing.documentTypes ?? ''
              });
            }

            return {
              id: occurrence.id ?? occurrence.Id,
              courseId: course.id ?? course.Id,
              courseCode: course.courseCode ?? course.CourseCode ?? '',
              courseName: course.courseTitle ?? course.CourseTitle ?? '',
              startDate: occurrence.startDate ?? occurrence.StartDate,
              endDate: occurrence.endDate ?? occurrence.EndDate,
              location: occurrence.location ?? occurrence.Location ?? '',
              availableSeats: occurrence.availableSeats ?? occurrence.AvailableSeats ?? 0,
              registrationEndDate: occurrence.registrationEndDate ?? occurrence.RegistrationEndDate ?? occurrence.registrationCloseDate ?? occurrence.RegistrationCloseDate ?? null,
              description: course.description ?? course.Description ?? '',
              assessment: course.assessment ?? course.Assessment ?? 'Practical and Knowledge Assessment',
              backgroundColor: occurrence.backgroundColor ?? occurrence.BackgroundColor ?? '#173b83',
              textColor: occurrence.textColor ?? occurrence.TextColor ?? '#ffffff'
            };
          })
          .filter((item: any) => item !== null);

        // Rebuild the filter options so the corporate popup offers the same controls as the public calendar.
        this.buildParticipantCalendarFilters();
        // Apply the current filter state and rebuild the visible FullCalendar events.
        this.applyParticipantCalendarFilters();
        // Stop showing the loading indicator after the data has been applied.
        this.isParticipantCalendarLoading = false;
      },
      error: (error) => {
        // Log the API error for troubleshooting without breaking the booking form.
        console.error('Participant calendar load error:', error);
        // Clear the occurrence list when loading fails.
        this.participantCalendarCourses = [];
        // Clear any events that may have remained from an earlier date range.
        this.participantCalendarOptions = {
          ...this.participantCalendarOptions,
          events: []
        };
        // Stop showing the loading indicator.
        this.isParticipantCalendarLoading = false;
      }
    });
  }

  // ============================================================
  // OPEN UOC / VOC SELECTION POPUP
  // ============================================================

  openParticipantCourseSelection(occurrence: any): void {

    // A participant row must be active before a course can be selected.
    if (this.participantCalendarIndex === null) {
      return;
    }

    const participant =
      this.participants[
      this.participantCalendarIndex
      ];

    if (!participant) {
      return;
    }

    /*
     * Check whether the user is continuing the same calendar-selection session.
     *
     * When starting fresh, restore the participant's already saved UOC / VOC
     * values before opening the newly clicked calendar course.
     */
    const isContinuingCalendarSelection =
      this.selectedParticipantOccurrence !== null;

    if (!isContinuingCalendarSelection) {

      this.participantModalUOCIds =
        participant.uocIds
          .filter(
            (id): id is number =>
              id !== null
          )
          .map(
            id =>
              Number(id)
          );

      this.participantModalVOCIds =
        participant.vocIds
          .filter(
            (id): id is number =>
              id !== null
          )
          .map(
            id =>
              Number(id)
          );
    }

    const selectedCalendarCourseId =
      Number(
        occurrence?.courseId ||
        0
      );

    if (!selectedCalendarCourseId) {
      return;
    }

    this.completeParticipantCourseSelectionOpen(
      occurrence
    );
  }


  // ============================================================
  // COMPLETE CALENDAR COURSE OPEN
  // ============================================================

  private completeParticipantCourseSelectionOpen(
    occurrence: any
  ): void {

    // Keep the newly clicked occurrence as the current course details record.
    this.selectedParticipantOccurrence =
      occurrence;

    // Keep the clicked occurrence date selected when the user goes back to the calendar.
    this.participantSelectedDate =
      this.toParticipantDateKey(
        new Date(
          occurrence.startDate
        )
      );

    const selectedCalendarCourseId =
      Number(
        occurrence.courseId
      );

    // Add the calendar-selected UOC to the temporary selection.
    if (
      selectedCalendarCourseId &&
      !this.participantModalUOCIds.some(
        id =>
          Number(id) ===
          selectedCalendarCourseId
      )
    ) {

      this.participantModalUOCIds.push(
        selectedCalendarCourseId
      );
    }

    // Reset the add-course dropdown controls before showing the details popup.
    this.participantSelectedUOCId =
      null;

    this.participantSelectedVOCId =
      null;


    this.participantCalendarErrorMessage =
      '';

    // Hide the calendar and display the UOC / VOC selection popup.
    this.showParticipantCalendarModal =
      false;

    this.showParticipantCourseSelectionModal =
      true;
  }

  // ============================================================
  // PARTICIPANT CALENDAR FILTERS
  // ============================================================

  buildParticipantCalendarFilters(): void {

    // Preserve previous selected state by course code.
    const previousState =
      new Map(
        this.participantCourseTypeFilters.map(
          filter => [
            filter.id,
            filter.selected
          ]
        )
      );


    const grouped =
      new Map<string, any>();

    this.participantCalendarCourses
      .forEach(
        course => {

          // Keep all course types visible in the calendar.
          const key =
            String(
              course.courseCode ||
              course.courseId
            );

          if (!grouped.has(key)) {

            grouped.set(
              key,
              {
                id: key,
                name:
                  `  ${course.courseName}`,
                selected:
                  previousState.has(key)
                    ? previousState.get(key)
                    : true,
                count: 0,
                backgroundColor:
                  course.backgroundColor,
                textColor:
                  course.textColor
              }
            );
          }

          grouped.get(key).count +=
            1;
        }
      );

    this.participantCourseTypeFilters =
      Array.from(
        grouped.values()
      );

    this.participantAvailableLocations =
      Array.from(
        new Set(
          this.participantCalendarCourses
            .map(
              course =>
                String(
                  course.location ||
                  ''
                ).trim()
            )
            .filter(
              location =>
                !!location
            )
        )
      ).sort();

    if (
      this.participantSelectedLocation &&
      !this.participantAvailableLocations.includes(
        this.participantSelectedLocation
      )
    ) {

      this.participantSelectedLocation =
        '';
    }
  }

  toggleParticipantCourseFilter(filter: any): void {
    // Toggle one course type just like the public Course Calendar filter panel.
    filter.selected = !filter.selected;
  }

  clearParticipantCalendarFilters(): void {
    // Reset every course filter and location filter to the public calendar default state.
    this.participantCourseTypeFilters.forEach(filter => filter.selected = true);
    this.participantSelectedLocation = '';
    this.applyParticipantCalendarFilters();
  }

  applyParticipantCalendarFilters(): void {

    const selectedCodes =
      new Set(
        this.participantCourseTypeFilters
          .filter(
            filter =>
              filter.selected
          )
          .map(
            filter =>
              String(filter.id)
          )
      );

    const participant =
      this.participantCalendarIndex !== null
        ? this.participants[
        this.participantCalendarIndex
        ]
        : null;

    this.participantFilteredCalendarCourses =
      this.participantCalendarCourses.filter(
        course => {

          const courseKey =
            String(
              course.courseCode ||
              course.courseId
            );

          const matchesCourse =
            selectedCodes.size === 0
              ? false
              : selectedCodes.has(
                courseKey
              );

          const matchesLocation =
            !this.participantSelectedLocation ||
            String(
              course.location ||
              ''
            ) ===
            this.participantSelectedLocation;

          return (
            matchesCourse &&
            matchesLocation
          );
        }
      );

    this.participantCalendarOptions = {
      ...this.participantCalendarOptions,

      events:
        this.participantFilteredCalendarCourses
          .map(
            course => ({
              id:
                String(course.id),

              title:
                `${course.courseCode} - (${course.courseCode}) ${course.courseName}`,

              start:
                course.startDate,

              end:
                course.endDate,

              backgroundColor:
                course.backgroundColor,

              borderColor:
                course.backgroundColor,

              textColor:
                course.textColor,

              calendarCode:
                course.courseCode,

              courseName:
                course.courseName
            })
          )
    };
  }

  // ============================================================
  // COURSE DETAILS STYLE UOC / VOC DROPDOWNS
  // ============================================================

  get participantAvailableUOCCourses(): any[] {
    // Hide the primary course and any UOCs already selected in the popup.
    const primaryId = Number(this.selectedParticipantOccurrence?.courseId || 0);

    const participant =
      this.participantCalendarIndex !== null
        ? this.participants[this.participantCalendarIndex]
        : null;

    return this.allUOCCourses.filter(course =>
      Number(course.id) !== primaryId &&
      !this.participantModalUOCIds.some(id => Number(id) === Number(course.id))
    );
  }

  get participantSelectedAdditionalUOCs(): any[] {
    // Return only additional UOC records for the left-side "Your Selection" summary.
    // The calendar course is already shown separately as the Primary Course in that section.
    const primaryId = Number(this.selectedParticipantOccurrence?.courseId || 0);

    return this.participantModalUOCIds
      .filter(id => Number(id) !== primaryId)
      .map(id => this.getUOCById(id))
      .filter(course => course != null);
  }


  // ============================================================
  // ALL SELECTED UOC COURSES FOR ADD UOC LIST
  // ============================================================

  get participantSelectedUOCs(): any[] {

    /*
     * Return every UOC currently selected in the course popup,
     * INCLUDING the primary course selected from the calendar.
     *
     * This is intentionally different from
     * participantSelectedAdditionalUOCs, because the Add UOC Course(s)
     * panel must visibly show the calendar-selected course as selected.
     */
    return this.participantModalUOCIds
      .map(id => this.getUOCById(id))
      .filter(course => course != null);
  }


  // ============================================================
  // CHECK PRIMARY CALENDAR UOC
  // ============================================================

  isParticipantPrimaryUOC(courseId: number): boolean {

    // The course clicked in the calendar is the mandatory primary UOC.
    return Number(courseId) ===
      Number(this.selectedParticipantOccurrence?.courseId || 0);
  }

  get participantAvailableVOCCourses(): any[] {
    // Hide VOCs that have already been selected.
    return this.allVOCCourses.filter(voc =>
      !this.participantModalVOCIds.some(
        id => Number(id) === Number(voc.id)
      )
    );
  }

  get participantSelectedAdditionalVOCs(): any[] {
    // Resolve selected VOC IDs to the same objects used by the main corporate form.
    return this.allVOCCourses.filter(voc =>
      this.participantModalVOCIds.some(id => Number(id) === Number(voc.id))
    );
  }

  get participantTotalSelectedCourses(): number {
    // Primary course is already contained in participantModalUOCIds, so the combined ID counts are the total.
    return this.participantModalUOCIds.length + this.participantModalVOCIds.length;
  }

  addParticipantSelectedUOC(): void {
    // Do nothing while the placeholder option is selected.
    if (this.participantSelectedUOCId === null) {
      return;
    }

    const selectedCourseId =
      Number(
        this.participantSelectedUOCId
      );

    const selectedCourse =
      this.getUOCById(
        selectedCourseId
      );

    if (!selectedCourse) {
      this.participantSelectedUOCId = null;
      return;
    }

    // Keep the first UOC selected from the calendar permanently in the list.
    const primaryCourseId =
      Number(
        this.selectedParticipantOccurrence?.courseId ||
        0
      );

    // Make sure the primary calendar UOC is always the first selected UOC.
    if (
      primaryCourseId &&
      !this.participantModalUOCIds.some(
        id =>
          Number(id) ===
          primaryCourseId
      )
    ) {

      this.participantModalUOCIds.unshift(
        primaryCourseId
      );
    }

    // Do not add the same UOC twice.
    const alreadySelected =
      this.participantModalUOCIds.some(
        id =>
          Number(id) ===
          selectedCourseId
      );

    if (alreadySelected) {
      this.participantSelectedUOCId = null;
      return;
    }

    // Keep the existing maximum of five UOC selections per participant.
    if (
      this.participantModalUOCIds.length >=
      5
    ) {

      this.participantCalendarErrorMessage =
        'A maximum of 5 UOC courses can be selected for one participant.';

      this.participantSelectedUOCId =
        null;

      return;
    }

    // Append the newly selected UOC.
    this.participantModalUOCIds.push(
      selectedCourseId
    );

    this.participantCalendarErrorMessage =
      '';

    this.participantSelectedUOCId =
      null;
  }

  removeParticipantSelectedUOC(courseId: number): void {
    // Keep the calendar-selected primary UOC protected from removal.
    const primaryCourseId =
      Number(this.selectedParticipantOccurrence?.courseId || 0);

    // Do not remove the very first / primary calendar-selected UOC.
    if (Number(courseId) === primaryCourseId) {
      return;
    }

    // Remove only an additional UOC selected by the user.
    this.participantModalUOCIds =
      this.participantModalUOCIds.filter(
        id => Number(id) !== Number(courseId)
      );

    // Clear any previous validation message.
    this.participantCalendarErrorMessage = '';
  }

  addParticipantSelectedVOC(): void {
    // Do nothing while the placeholder option is selected.
    if (this.participantSelectedVOCId === null) {
      return;
    }
    // Reuse the existing limit validation and selection logic.
    this.toggleParticipantModalVOC(Number(this.participantSelectedVOCId));
    // Return the dropdown to its placeholder after adding the selected item.
    this.participantSelectedVOCId = null;
  }

  removeParticipantSelectedVOC(vocId: number): void {
    // Remove the selected VOC from the temporary popup state.
    this.participantModalVOCIds = this.participantModalVOCIds.filter(id => Number(id) !== Number(vocId));
    this.participantCalendarErrorMessage = '';
  }

  // ============================================================
  // UOC / VOC CHECKBOX HELPERS
  // ============================================================

  isParticipantModalUOCSelected(courseId: number): boolean {
    // Return true when this UOC is selected in the temporary popup state.
    return this.participantModalUOCIds.some(id => Number(id) === Number(courseId));
  }

  toggleParticipantModalUOC(courseId: number): void {
    // Get the primary UOC represented by the calendar event.
    const primaryCourseId =
      Number(this.selectedParticipantOccurrence?.courseId || 0);

    // The primary calendar-selected UOC must never be removed.
    if (Number(courseId) === primaryCourseId) {
      // Restore it as the first item if required.
      if (
        primaryCourseId &&
        !this.participantModalUOCIds.some(
          id => Number(id) === primaryCourseId
        )
      ) {
        this.participantModalUOCIds.unshift(
          primaryCourseId
        );
      }

      return;
    }

    // Find whether the clicked additional UOC is already selected.
    const existingIndex =
      this.participantModalUOCIds.findIndex(
        id => Number(id) === Number(courseId)
      );

    // Remove only an additional UOC that is already selected.
    if (existingIndex >= 0) {
      this.participantModalUOCIds.splice(
        existingIndex,
        1
      );

      return;
    }

    // Keep the existing corporate form maximum of five UOC selections per participant.
    if (this.participantModalUOCIds.length >= 5) {
      this.participantCalendarErrorMessage =
        'A maximum of 5 UOC courses can be selected for one participant.';

      return;
    }

    // Add the newly selected UOC after the primary UOC.
    this.participantModalUOCIds.push(
      Number(courseId)
    );

    // Clear any previous validation message.
    this.participantCalendarErrorMessage = '';
  }

  isParticipantModalVOCSelected(vocId: number): boolean {
    // Return true when this VOC is selected in the temporary popup state.
    return this.participantModalVOCIds.some(id => Number(id) === Number(vocId));
  }

  toggleParticipantModalVOC(vocId: number): void {
    // Find whether the clicked VOC is already selected.
    const existingIndex = this.participantModalVOCIds.findIndex(
      id => Number(id) === Number(vocId)
    );

    // Remove an already selected VOC.
    if (existingIndex >= 0) {
      this.participantModalVOCIds.splice(existingIndex, 1);
      return;
    }

    // Keep the existing corporate form maximum of five VOC selections per participant.
    if (this.participantModalVOCIds.length >= 5) {
      this.participantCalendarErrorMessage =
        'A maximum of 5 VOC courses can be selected for one participant.';
      return;
    }

    // Add the newly selected VOC.
    this.participantModalVOCIds.push(Number(vocId));
    this.participantCalendarErrorMessage = '';
  }

  // ============================================================
  // CONTINUE FROM UOC / VOC SELECTION
  // ============================================================

  applyParticipantCalendarSelection(): void {
    // Ensure a participant row and calendar occurrence are available.
    if (this.participantCalendarIndex === null || !this.selectedParticipantOccurrence) {
      return;
    }
    // Require at least one UOC or VOC before continuing.
    if (this.participantModalUOCIds.length === 0 && this.participantModalVOCIds.length === 0) {
      this.participantCalendarErrorMessage = 'Please select at least one UOC or VOC course.';
      return;
    }
    // Get the participant row that opened the calendar.
    const participant = this.participants[this.participantCalendarIndex];
    // Expand the visible UOC column count when the popup contains more selections.
    if (this.participantModalUOCIds.length > this.numberOfUOC) {
      this.numberOfUOC = Math.min(this.participantModalUOCIds.length, 5);
      this.updateUOCCount();
    }
    // Expand the visible VOC column count when the popup contains more selections.
    if (this.participantModalVOCIds.length > this.numberOfVOC) {
      this.numberOfVOC = Math.min(this.participantModalVOCIds.length, 5);
      this.updateVOCCount();
    }
    // Copy the selected UOC IDs into the participant's existing main-form slots.
    participant.uocIds = Array(this.numberOfUOC).fill(null);
    this.participantModalUOCIds.slice(0, this.numberOfUOC).forEach((id, index) => {
      participant.uocIds[index] = id;
    });

    // Copy the selected VOC IDs into the participant's existing main-form slots.
    participant.vocIds = Array(this.numberOfVOC).fill(null);
    this.participantModalVOCIds.slice(0, this.numberOfVOC).forEach((id, index) => {
      participant.vocIds[index] = id;
    });
    // Save the occurrence date so this participant's calendar selection can be restored later.
    participant.calendarDate = this.toParticipantDateKey(new Date(this.selectedParticipantOccurrence.startDate));
    // Save the occurrence identifier for future reopening/editing.
    participant.calendarOccurrenceId = Number(this.selectedParticipantOccurrence.id);
    // Save the primary course identifier represented by the calendar occurrence.
    participant.calendarCourseId = Number(this.selectedParticipantOccurrence.courseId);
    // Recalculate Date Training Required from all participant calendar dates.
    this.updateDateTrainingRequiredFromParticipants();
    // Close the selection popup after the values are copied back to the main form.
    this.showParticipantCourseSelectionModal = false;
    this.selectedParticipantOccurrence = null;
    this.participantCalendarErrorMessage = '';
  }

  // ============================================================
  // BACK TO CALENDAR
  // ============================================================

  backToParticipantCalendar(): void {

    // Close only the course-selection popup.
    this.showParticipantCourseSelectionModal = false;


    /*
     * Keep the date of the currently selected occurrence highlighted
     * when returning to the participant calendar.
     */
    if (this.selectedParticipantOccurrence?.startDate) {

      this.participantSelectedDate =
        this.toParticipantDateKey(
          new Date(this.selectedParticipantOccurrence.startDate)
        );
    }


    /*
     * Rebuild the calendar options so FullCalendar renders the events
     * again. This allows eventDidMount to re-apply the selected
     * occurrence highlight while retaining all temporary UOC / VOC
     * selections in this popup flow.
     */
    this.participantCalendarOptions = {

      ...this.participantCalendarOptions,

      initialDate:
        this.selectedParticipantOccurrence?.startDate ||
        new Date(),

      events:
        this.participantFilteredCalendarCourses.map(
          course => ({

            id:
              String(course.id),

            title:
              `${course.courseCode} - (${course.courseCode}) ${course.courseName}`,

            start:
              course.startDate,

            end:
              course.endDate,

            backgroundColor:
              course.backgroundColor,

            borderColor:
              course.backgroundColor,

            textColor:
              course.textColor,

            calendarCode:
              course.courseCode,

            courseName:
              course.courseName
          })
        )
    };


    // Reopen the calendar without clearing the current popup selections.
    this.showParticipantCalendarModal = true;
  }

  // ============================================================
  // CLOSE COURSE SELECTION
  // ============================================================

  closeParticipantCourseSelection(): void {
    // Close the second popup without applying temporary changes to the participant row.
    this.showParticipantCourseSelectionModal = false;
    // Clear temporary popup-only values.
    this.selectedParticipantOccurrence = null;
    this.participantCalendarErrorMessage = '';
  }

  // ============================================================
  // DATE TRAINING REQUIRED
  // ============================================================

  updateDateTrainingRequiredFromParticipants(): void {
    // Collect every participant date that was selected from the calendar.
    const selectedDates = this.participants
      .map(participant => participant.calendarDate)
      .filter(date => !!date)
      .sort();
    // Set the earliest selected participant date as Date Training Required.
    this.dateTrainingRequired = selectedDates.length > 0 ? selectedDates[0] : '';
  }

  // ============================================================
  // PARTICIPANT DATE DISPLAY
  // ============================================================

  getParticipantCalendarDate(participant: CorporateParticipant): string {
    // Show a default label when the participant has no calendar selection yet.
    if (!participant.calendarDate) {
      return 'Select Calendar';
    }
    // Parse the YYYY-MM-DD value without timezone shifting the displayed day.
    const parts = participant.calendarDate.split('-').map(Number);
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    // Return Australian-style date text.
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // ============================================================
  // PARTICIPANT CALENDAR SELECTED DATE
  // ============================================================

  selectParticipantCalendarDate(date: string): void {
    // Keep the same selected-day behaviour as the public Course Calendar.
    this.participantSelectedDate = date;
  }

  // ============================================================
  // PARTICIPANT CALENDAR EVENT COLOURS
  // ============================================================

  applyParticipantEventElementColors(
    element: HTMLElement,
    backgroundColor: string,
    textColor: string
  ): void {
    // Apply the database/VETenrol event colours exactly as the public Course Calendar does.
    element.style.backgroundColor = backgroundColor;
    element.style.borderColor = backgroundColor;
    element.style.color = textColor;
  }

  // ============================================================
  // PARTICIPANT CALENDAR NAVIGATION
  // ============================================================

  goParticipantCalendarToday(calendar: any): void {
    // Move the popup calendar back to the current date.
    calendar.getApi().today();
  }

  goParticipantCalendarPrevious(calendar: any): void {
    // Move the popup calendar to the previous date range.
    calendar.getApi().prev();
  }

  goParticipantCalendarNext(calendar: any): void {
    // Move the popup calendar to the next date range.
    calendar.getApi().next();
  }

  changeParticipantCalendarView(calendar: any, view: 'dayGridMonth' | 'dayGridWeek' | 'dayGridDay'): void {
    // Switch between Month, Week and Day views in the popup calendar.
    this.participantActiveCalendarView = view;
    calendar.getApi().changeView(view);
  }

  // ============================================================
  // DATE KEY HELPER
  // ============================================================

  toParticipantDateKey(date: Date): string {
    // Format a Date as YYYY-MM-DD using local date values.
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    // Return the API/input-compatible date key.
    return `${year}-${month}-${day}`;
  }

  // ============================================================
  // RESET
  // ============================================================

  resetCorporateForm(): void {

    this.isBookNowSubmitted = false;
    // ==========================================================
    // CORPORATE CLIENT DETAILS
    // ==========================================================

    this.companyName = '';

    this.companyEmail = '';

    this.requestedBy = '';

    this.requestedByPhone = '';

    this.onSiteSupervisor = '';

    this.onSiteSupervisorPhone = '';

    this.siteLocationAddress = '';

    this.siteLocationSelection = '';

    this.dateRequested = new Date().toISOString().split('T')[0];

    this.dateTrainingRequired = '';

    this.copyOfAwardsRequired =
      'YES';

    this.isMetroClient =
      'NO';

    this.uploadAwardEvidenceToPegasus =
      'NO';


    // ==========================================================
    // BOOKING CONFIGURATION
    // ==========================================================

    this.numberOfParticipants =
      1;

    this.numberOfUOC =
      2;

    this.numberOfVOC =
      2;


    this.numberOfOtherRows =
      1;


    // ==========================================================
    // TABLE DATA
    // ==========================================================

    this.participants = [
      this.createParticipant(),

    ];

    this.otherRows = [
      this.createOtherRow()
    ];

    this.additionalComments =
      '';

    this.corporateSuccessMessage =
      '';

    this.corporateErrorMessage =
      '';

    // Reset participant calendar popup state together with the corporate form.
    this.showParticipantCalendarModal = false;
    this.showParticipantCourseSelectionModal = false;
    this.participantCalendarIndex = null;
    this.selectedParticipantOccurrence = null;
    this.participantModalUOCIds = [];
    this.participantModalVOCIds = [];
    this.participantCalendarErrorMessage = '';
  }


  // ============================================================
  // AVAILABLE UOC OPTIONS FOR DYNAMIC PARTICIPANT TABLE CELLS
  // ============================================================

  getParticipantUOCOptions(
    participant: CorporateParticipant,
    uocIndex: number
  ): any[] {
    const selectedId = Number(participant.uocIds[uocIndex] || 0);
    const selectionKey = participant.uocIds.join(',');
    const existingByRow = this.participantUOCOptionsCache.get(participant);
    const saved = existingByRow?.get(uocIndex);

    if (
      saved &&
      saved.source === this.allUOCCourses &&
      saved.selectionKey === selectionKey
    ) {
      return saved.options;
    }

    // Keep current value visible when the dropdown search changes.
    const options = [...this.allUOCCourses];
    const selectedCourse = this.getUOCById(selectedId);
    if (selectedCourse && !options.some(c => Number(c.id) === selectedId)) {
      options.unshift(selectedCourse);
    }

    // Only hide a UOC if it is selected in a DIFFERENT column of this participant.
    const available = options.filter(course =>
      course != null &&
      !this.isUOCSelected(participant, Number(course.id), uocIndex)
    );

    const rowCache = existingByRow ?? new Map();
    rowCache.set(uocIndex, {
      source: this.allUOCCourses,
      selectionKey,
      options: available
    });
    this.participantUOCOptionsCache.set(participant, rowCache);
    return available;
  }

  // ============================================================
  // GET UOC BY ID
  // ============================================================

  getUOCById(
    id: number | null
  ): any {
    if (id === null || id === undefined) {
      return null;
    }

    const courseId = Number(id);
    return this.uocCourseCache.get(courseId) ||
      this.allUOCCourses.find(course => Number(course.id) === courseId) ||
      null;
  }


  // ============================================================
  // GET VOC BY ID
  // ============================================================

  getVOCById(
    id: number | null
  ): any {

    if (
      id === null ||
      id === undefined
    ) {

      return null;
    }


    return this
      .allVOCCourses
      .find(
        voc =>
          Number(voc.id) ===
          Number(id)
      );
  }


  // ============================================================
  // CREATE CORPORATE BOOKING JSON
  // ============================================================

  createCorporateBookingJson(): any {

    return {

      enquiryType:
        'CORPORATE_BOOKING',

      clientType:
        'CORPORATE',


      // ========================================================
      // CLIENT DETAILS
      // ========================================================

      clientDetails: {

        companyName:
          this.companyName?.trim() || '',

        companyEmail:
          this.companyEmail?.trim() || '',

        requestedBy:
          this.requestedBy?.trim() || '',

        requestedByPhone:
          this.requestedByPhone?.trim() || '',

        onSiteSupervisor:
          this.onSiteSupervisor?.trim() || '',

        onSiteSupervisorPhone:
          this.onSiteSupervisorPhone?.trim() || '',

        siteLocationAddress:
          this.siteLocationAddress?.trim() || '',

        dateRequested:
          this.dateRequested || null,

        dateTrainingRequired:
          this.dateTrainingRequired || null,

        copyOfAwardsRequired:
          this.copyOfAwardsRequired,

        isMetroClient:
          this.isMetroClient,

        uploadAwardEvidenceToPegasus:
          this.isMetroClient === 'YES'
            ? this.uploadAwardEvidenceToPegasus
            : 'NO'
      },


      // ========================================================
      // BOOKING CONFIGURATION
      // ========================================================

      bookingConfiguration: {

        numberOfParticipants:
          this.participants.length,

        numberOfUOC:
          this.numberOfUOC,

        numberOfVOC:
          this.numberOfVOC,


        numberOfOtherRows:
          this.otherRows.length,

        numberOfOtherColumns:
          5
      },


      // ========================================================
      // PARTICIPANTS
      // ========================================================

      participants:
        this.participants.map(
          (
            participant,
            participantIndex
          ) => {

            return {

              participantNo:
                participantIndex + 1,

              fullName:
                participant.fullName.trim(),

              // Date selected from the participant calendar for this row.
              trainingDate:
                participant.calendarDate || null,

              // Calendar occurrence selected for this participant.
              calendarOccurrenceId:
                participant.calendarOccurrenceId,

              // Primary UOC represented by the selected calendar occurrence.
              calendarCourseId:
                participant.calendarCourseId,


              // UOC
              uocCourses:
                participant.uocIds
                  .map(
                    (
                      id,
                      uocIndex
                    ) => {

                      const course =
                        this.getUOCById(id);


                      if (!course) {
                        return null;
                      }


                      return {

                        slot:
                          uocIndex + 1,

                        courseId:
                          course.id,

                        courseCode:
                          course.courseCode,

                        courseTitle:
                          course.courseTitle,

                        description:
                          course.description,

                        totalAmount:
                          course.totalAmount,

                        currencyCode:
                          course.currencyCode,

                        documentTypes:
                          course.documentTypes
                      };
                    }
                  )
                  .filter(
                    course =>
                      course !== null
                  ),


              // VOC
              vocCourses:
                participant.vocIds
                  .map(
                    (
                      id,
                      vocIndex
                    ) => {

                      const voc =
                        this.getVOCById(id);


                      if (!voc) {
                        return null;
                      }


                      return {

                        slot:
                          vocIndex + 1,

                        vocId:
                          voc.id,

                        vocCode:
                          voc.vocCode,

                        vocName:
                          voc.vocName,

                        description:
                          voc.description,

                        documentTypes:
                          voc.documentTypes,

                        fkParentCourseId:
                          voc.fkParentCourseId
                      };
                    }
                  )
                  .filter(
                    voc =>
                      voc !== null
                  ),
            };
          }
        ),


      // ========================================================
      // OTHER ITEMS
      // ========================================================

      others:
        this.otherRows.map(
          (
            row,
            rowIndex
          ) => {

            return {

              rowNo:
                rowIndex + 1,

              values:
                row.values.map(
                  (
                    value,
                    valueIndex
                  ) => {

                    return {

                      slot:
                        valueIndex + 1,

                      value:
                        value?.trim() || ''
                    };
                  }
                )
            };
          }
        ),


      // ========================================================
      // COMMENTS
      // ========================================================

      additionalComments:
        this.additionalComments?.trim()
        || '',


      createdDate:
        new Date().toISOString()
    };
  }


  // ============================================================
  // VALIDATION
  // ============================================================

  validateCorporateBooking(): boolean {

    this.corporateErrorMessage =
      '';


    if (!this.companyName?.trim()) {

      this.corporateErrorMessage =
        'Please enter the company name.';

      return false;
    }


    if (!this.companyEmail?.trim()) {

      this.corporateErrorMessage =
        'Please enter the requested by company Email.';

      return false;
    }

    if (!this.requestedByPhone?.trim()) {
      this.corporateErrorMessage =
        'Please enter the requested by phone number.';

      return false;
    }




    const invalidParticipant =
      this.participants.some(
        participant =>
          !participant.fullName?.trim()
      );


    if (invalidParticipant) {

      this.corporateErrorMessage =
        'Please enter the name for every participant.';

      return false;
    }


    return true;
  }

  // ============================================================
  // SAVE CORPORATE BOOKING
  // ============================================================

  sendEnquire(): void {

    this.corporateSuccessMessage =
      '';

    this.corporateErrorMessage =
      '';


    if (
      !this
        .validateCorporateBooking()
    ) {

      return;
    }

    // Never silently omit a UOC ID that has not been resolved from the API.
    const unresolvedUOCIds = [...new Set(
      this.participants.flatMap(participant => participant.uocIds)
        .filter((id): id is number => id !== null && Number(id) > 0)
    )].filter(id => !this.getUOCById(id));

    if (unresolvedUOCIds.length) {
      this.corporateErrorMessage =
        'Some selected UOC course details are unavailable. Please reselect those courses and try again.';
      return;
    }

    const bookingJson =
      this
        .createCorporateBookingJson();


    console.log(
      'Corporate Booking JSON:',
      bookingJson
    );


    const enquiryModel = {

      id: 0,

      body:
        JSON.stringify(
          bookingJson
        ),

      sentBy:
        this.companyEmail,

      reviewedBy:
        null,

      reviewedDate:
        null,

      isApproved:
        false,

      isActive:
        true,

      clientType: 'CORPORATE'
    };


    this.isSavingCorporateBooking =
      true;


    this.adminService
      .setXBooking(
        enquiryModel,
        'INSERT'
      )
      .subscribe({

        next: (response) => {

          this.isSavingCorporateBooking = false;

          this.resetCorporateForm();

          this.corporateSuccessMessage =
            'Thank you for your enquiry! We have received your enquiry successfully. One of our team members will be in touch with you shortly.';

          this.showEnquirySuccessPopup = true;
        },


        error: (error) => {

          console.error(
            'Corporate booking save error:',
            error
          );


          this.isSavingCorporateBooking =
            false;


          this.corporateErrorMessage =
            'Unable to save booking details. Please try again.';
        }
      });
  }

  // Close enquiry success popup
  closeEnquirySuccessPopup(): void {

    // Hide success popup
    this.showEnquirySuccessPopup = false;

    // Navigate to home page
    this.router.navigate(['/']);
  }


  // Save corporate booking
  saveCorporateBooking(): void {

    // Validate course selection
    const isValid = this.participants.every(participant =>
      participant.uocIds.some(id => id !== null) ||
      participant.vocIds.some(id => id !== null)
    );

    // Handle missing course selection
    if (!isValid) {

      this.corporateErrorMessage =
        'Please select at least 1 course for each participant.';

      return;
    }

    // Check participant names
    const invalidParticipant =
      this.participants.some(
        participant =>
          !participant.fullName?.trim()
      );


    // Handle missing participant name
    if (invalidParticipant) {

      this.corporateErrorMessage =
        'Please enter the name for every participant.';

    }
    else {

      // Reset error message
      this.corporateErrorMessage = "";

      // Mark Book Now submission
      this.isBookNowSubmitted = true;
    }
  }


  // Check duplicate UOC selection
  isUOCSelected(
    participant: CorporateParticipant,
    courseId: number,
    currentIndex: number
  ): boolean {

    return participant.uocIds.some(
      (selectedId, index) =>
        index !== currentIndex &&
        Number(selectedId) === Number(courseId)
    );
  }


  // Check duplicate VOC selection
  isVOCSelected(
    participant: CorporateParticipant,
    vocId: number,
    currentIndex: number
  ): boolean {

    return participant.vocIds.some(
      (selectedId, index) =>
        index !== currentIndex &&
        Number(selectedId) === Number(vocId)
    );
  }


  // Handle site location selection
  onSiteLocationSelectionChange(): void {

    // Handle custom location
    if (this.siteLocationSelection === 'OTHER') {

      this.siteLocationAddress = '';

      return;
    }

    // Store selected location
    this.siteLocationAddress =
      this.siteLocationSelection || '';
  }


  // Load site locations
  loadSiteLocations(): void {

    // Start loading
    this.isLoadingSiteLocations =
      true;

    this.adminService
      .getXSiteLocations(
        'GET_ACTIVE'
      )
      .subscribe({

        next: (response: any[]) => {

          // Store site locations
          this.siteLocations =
            response || [];

          // Stop loading
          this.isLoadingSiteLocations =
            false;
        },

        error: (error) => {

          console.error(
            'Site locations load error:',
            error
          );

          // Reset site locations
          this.siteLocations =
            [];

          // Stop loading
          this.isLoadingSiteLocations =
            false;
        }

      });
  }
}
